<template>
  <div class="rrd-recognition-cell" role="list" aria-label="Recognition results by economy">
    <!-- compact list in table cell -->
    <div class="rrd-mini-list" role="list">
      <div
        v-for="r in sorted"
        :key="r.countryCode"
        class="rrd-mini-row"
        :class="statusClass(r.status)"
        role="listitem"
      >
        <span class="rrd-country">{{ r.countryCode }}</span>
        <span class="rrd-mini-icon" aria-hidden="true">{{ statusIcon(r.status) }}</span>
      </div>
    </div>

    <!-- ONE details icon (for whole cell) -->
    <button
      v-if="sorted.length"
      type="button"
      class="rrd-details-btn rrd-details-btn--single rrd-info-btn"
      aria-label="Open recognition details"
      @click="openModalAll"
    >
      <span class="rrd-info-icon">i</span>
    </button>

    <span v-if="!sorted.length" class="text-muted small">—</span>

    <!-- Modal (ALL countries) -->
    <div v-if="modalOpen" class="rrd-modal-backdrop" @click.self="closeModal">
      <div class="rrd-modal" role="dialog" aria-modal="true">
        <div class="rrd-modal-header">
          <div class="rrd-modal-title">
            Recognition details
          </div>

          <button type="button" class="rrd-modal-close" aria-label="Close" @click="closeModal">
            ×
          </button>
        </div>

        <div class="rrd-modal-body">
          <table class="rrd-modal-table">
            <thead>
            <tr>
              <th>Country</th>
              <th>Status</th>
              <th>Cycle</th>
              <th>Recognized at</th>
              <th>Non-recognition reason</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="r in sorted" :key="'m-' + r.countryCode">
              <td class="rrd-td-code">{{ r.countryCode }}</td>
              <td>
                  <span class="rrd-status">
                    <span class="rrd-mini-icon" aria-hidden="true">{{ statusIcon(r.status) }}</span>
                    {{ decisionText(r.status) }}
                  </span>
              </td>
              <td>{{ r.cycle || "—" }}</td>
              <td>{{ r.recognized_at || "—" }}</td>
              <td class="rrd-td-reason" v-html="r.non_recognition_reason"/>
            </tr>
            </tbody>
          </table>
        </div>

        <div class="rrd-modal-footer">
          <button type="button" class="rrd-modal-btn" @click="closeModal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "RecognitionResultsCell",

  props: {
    results: { type: Array, default: () => [] },
  },

  data() {
    return {
      modalOpen: false,
    };
  },

  computed: {
    sorted() {
      return (this.results || []).slice().sort((a, b) => {
        const ac = String(a.countryCode || "");
        const bc = String(b.countryCode || "");
        return ac.localeCompare(bc);
      });
    },
  },

  methods: {
    openModalAll() {
      this.modalOpen = true;
    },

    closeModal() {
      this.modalOpen = false;
    },

    statusIcon(s) {
      if (s === "FULL") return "✓";
      if (s === "NOT") return "✕";
      if (s === "PARTIAL" || s === "CONDITIONAL") return "!";
      if (s === "PENDING") return "…";
      return "•";
    },

    decisionText(s) {
      if (s === "FULL") return "Fully recognized";
      if (s === "NOT") return "Not recognized";
      if (s === "PARTIAL") return "Partially recognized";
      if (s === "CONDITIONAL") return "Conditionally recognized";
      if (s === "PENDING") return "Pending";
      return "Unknown";
    },

    statusClass(s) {
      return `is-${String(s || "UNKNOWN").toLowerCase()}`;
    },
  },

  mounted() {
    this._escHandler = (e) => {
      if (e.key === "Escape" && this.modalOpen) this.closeModal();
    };
    document.addEventListener("keydown", this._escHandler);
  },

  beforeDestroy() {
    document.removeEventListener("keydown", this._escHandler);
  },
};
</script>

<style scoped>
/* Layout: mini list + one icon aligned right */
.rrd-recognition-cell {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: start;
  font-size: 12px;
}

/* Mini list in table cell */
.rrd-mini-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.rrd-mini-row {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  line-height: 1;
}

.rrd-country {
  font-weight: 700;
  letter-spacing: 0.2px;
  min-width: 34px;
}

.rrd-mini-icon {
  font-size: 20px;
  line-height: 1;
}

/* Single details button */
.rrd-details-btn {
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0 4px;
  font-size: 20px;
  line-height: 1;
  opacity: 0.75;
}
.rrd-details-btn:hover,
.rrd-details-btn:focus {
  opacity: 1;
  outline: none;
}
.rrd-details-btn--single {
  margin-top: 2px;
}

/* Status colors (same as before, but apply to icons) */
.is-full .rrd-mini-icon { color: #198754; }
.is-not .rrd-mini-icon { color: #dc3545; }
.is-partial .rrd-mini-icon,
.is-conditional .rrd-mini-icon { color: #d39e00; }
.is-pending .rrd-mini-icon { color: #0d6efd; }
.is-unknown .rrd-mini-icon { color: #6c757d; }

/* Modal (re-use your styles, add table) */
.rrd-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.45);
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
}

.rrd-modal {
  width: 920px;
  max-width: 100%;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.30);
  overflow: hidden;
}

.rrd-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(0,0,0,0.08);
}

.rrd-modal-title {
  font-weight: 600;
  color: #111827;
  font-size: 14px;
}

.rrd-modal-close {
  border: 0;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  opacity: 0.65;
}
.rrd-modal-close:hover { opacity: 1; }

.rrd-modal-body {
  padding: 12px 14px;
  overflow: auto;
}

.rrd-modal-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.rrd-modal-table th {
  text-align: left;
  padding: 10px 8px;
  border-bottom: 1px solid rgba(0,0,0,0.10);
  white-space: nowrap;

  font-size: 12px;
  text-transform: uppercase;

  color: #6b7280;
  margin-bottom: 6px;
  min-height: 34px;

}

.rrd-modal-table td {
  padding: 10px 8px;
  border-bottom: 1px solid rgba(0,0,0,0.06);
  vertical-align: top;
}

.rrd-td-code {
  font-weight: 600;
  white-space: nowrap;
}

.rrd-td-reason {
  max-width: 360px;
  white-space: normal;
}

.rrd-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #111827;
}

.rrd-modal-footer {
  padding: 12px 14px;
  border-top: 1px solid rgba(0,0,0,0.08);
  display: flex;
  justify-content: flex-end;
}

.rrd-modal-btn {
  border: 1px solid rgba(0,0,0,0.15);
  background: #fff;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: 700;
}
.rrd-modal-btn:hover {
  background: rgba(0,0,0,0.03);
}
.rrd-info-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid var(--rrd-border, #e6eaf2);
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  opacity: 0.9;
}

.rrd-info-btn:hover {
  background: #fbfcff;
  opacity: 1;
}



.rrd-info-icon {
  font-size: 15px;
  font-weight: 900;
  color: #1f2937;
  line-height: 1;
}
</style>
